/**
 * TITULARIDAD (B1) — lectura local-first de la PROYECCIÓN materializada.
 *
 * El cliente **solo lee**: `entitlement_state` y `entitled_course` las escribe
 * el materializador del servidor y el write-path de sync rechaza cualquier op
 * sobre ellas (si no, un alumno se auto-concedería el acceso).
 *
 * Estas filas existen para que la app pueda ser HONESTA —«no puedes ver la
 * grabación porque tienes un pago pendiente, y esto viene de la política de tu
 * cohorte»— en vez de mostrar un error seco. La barrera real está en el
 * servidor: los gates REST y el write-path.
 */

import {
  and,
  entitled_course,
  entitlement_state,
  eq,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

/** Claves de caché de la titularidad (TanStack Query). */
export const entitlementQueryKeys = {
  /** Prefijo: invalidarlo cascadea a estados, políticas y simulación. */
  all: ["entitlement"] as const,
  states: (orgId: string | undefined) => ["entitlement", "states", orgId] as const,
  courses: (userId: string | undefined) => ["entitlement", "courses", userId] as const,
  /** REST de administración (no PowerSync): la cascada configurada. */
  policies: (orgId: string | undefined) => ["entitlement", "policies", orgId] as const,
  /** REST de administración: «a quién bloquearía esto» (simulación). */
  preview: (orgId: string | undefined) => ["entitlement", "preview", orgId] as const,
};

/** Todos mis estados de titularidad en una academia (academia + por cohorte). */
export function getEntitlementStatesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(entitlement_state).where(eq(entitlement_state.organization_id, orgId));
}

/** Mi fila de titularidad de un curso (`full` | `frozen`), si la tengo. */
export function getEntitledCourseQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db.select().from(entitled_course).where(eq(entitled_course.course_id, courseId)).limit(1);
}

/** Mi estado agregado de la academia (el que pinta el aviso global). */
export function getOrgEntitlementStateQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(entitlement_state)
    .where(
      and(eq(entitlement_state.organization_id, orgId), eq(entitlement_state.scope_level, "org")),
    )
    .limit(1);
}
